import React from 'react';
import { Award, Target, Heart, Users, Building, Clock } from 'lucide-react';

const About: React.FC = () => {
  const values = [
    {
      icon: <Award className="w-12 h-12 text-golden" />,
      title: "Excellence",
      description: "We maintain the highest standards in every aspect of our work, from property selection to final delivery."
    },
    {
      icon: <Target className="w-12 h-12 text-golden" />,
      title: "Innovation",
      description: "We combine traditional restoration techniques with modern technology to achieve the best results."
    },
    {
      icon: <Heart className="w-12 h-12 text-golden" />,
      title: "Passion",
      description: "Our love for historic architecture drives us to preserve and enhance these beautiful properties."
    },
    {
      icon: <Users className="w-12 h-12 text-golden" />,
      title: "Integrity",
      description: "We build lasting relationships based on trust, transparency, and mutual respect."
    }
  ];

  const whyOldHouses = [
    {
      icon: <Building className="w-10 h-10 text-golden" />,
      title: "Unique Character",
      description: "Historic properties offer architectural details and craftsmanship that cannot be replicated in new construction."
    },
    {
      icon: <Clock className="w-10 h-10 text-golden" />,
      title: "Proven Durability",
      description: "These buildings have stood the test of time, demonstrating their structural integrity and quality."
    },
    {
      icon: <Award className="w-10 h-10 text-golden" />,
      title: "Investment Value",
      description: "Well-restored historic properties often appreciate faster than new construction due to their scarcity and appeal."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-r from-dark-gray to-gray-800 text-white">
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 fade-in-up">About Us</h1>
          <p className="text-xl md:text-2xl text-golden max-w-3xl mx-auto fade-in-up stagger-1">
            We are passionate about preserving architectural heritage while creating valuable investments for our partners.
          </p>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Vision */}
            <div className="text-center lg:text-left fade-in-left">
              <div className="w-20 h-20 bg-golden rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-6 scale-in hover-scale">
                <Target className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-dark-gray mb-6">Our Vision</h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                To become the leading investment partnership firm specializing in historic property 
                restoration, setting the standard for excellence in preserving architectural heritage 
                while delivering exceptional returns for our partners.
              </p>
              <p className="text-lg text-gray-600 mt-4 leading-relaxed">
                We envision a future where every historic property is not just preserved, but 
                enhanced to meet modern living standards while maintaining its unique character 
                and cultural significance.
              </p>
            </div>

            {/* Mission */}
            <div className="text-center lg:text-left fade-in-right">
              <div className="w-20 h-20 bg-golden rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-6 scale-in hover-scale">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-dark-gray mb-6">Our Mission</h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                To identify, acquire, and transform historic properties into exceptional 
                investments through expert restoration and strategic modernization, creating 
                value for our partners while preserving architectural heritage.
              </p>
              <p className="text-lg text-gray-600 mt-4 leading-relaxed">
                We are committed to maintaining the highest standards of craftsmanship, 
                using sustainable practices, and ensuring every project exceeds expectations 
                in both quality and return on investment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Old Houses Section */}
      <section className="section-padding bg-light-beige">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4 fade-in-up">Why Old Houses?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up stagger-1">
              Historic properties offer unique opportunities that new construction simply cannot match. 
              Here's why we believe in investing in the past to secure the future.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyOldHouses.map((item, index) => (
              <div key={index} className={`bg-white p-8 rounded-lg shadow-lg text-center card-hover fade-in-up stagger-${index + 1}`}>
                <div className="mb-4 hover-rotate">{item.icon}</div>
                <h3 className="text-xl font-semibold text-dark-gray mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl mx-auto fade-in-up hover-glow">
              <h3 className="text-2xl font-bold text-dark-gray mb-4">Modern Touches, Timeless Character</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our restoration approach focuses on preserving the architectural integrity and 
                historical significance of each property while seamlessly integrating modern 
                amenities, energy-efficient systems, and contemporary design elements. We believe 
                that the best of both worlds—historic charm and modern comfort—creates properties 
                that are not only beautiful but also highly desirable and valuable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4 fade-in-up">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up stagger-1">
              These fundamental principles guide every decision we make and every project we undertake.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className={`text-center p-6 card-hover fade-in-up stagger-${index + 1}`}>
                <div className="mb-4 flex justify-center hover-rotate">{value.icon}</div>
                <h3 className="text-xl font-semibold text-dark-gray mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-dark-gray text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 fade-in-up">Our Team</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto fade-in-up stagger-1">
              Meet the experts behind our success—a dedicated team of professionals passionate 
              about historic preservation and real estate investment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-6 rounded-lg text-center card-hover fade-in-up stagger-1">
              <div className="w-24 h-24 bg-golden rounded-full flex items-center justify-center mx-auto mb-4 scale-in hover-scale">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Investment Specialists</h3>
              <p className="text-gray-300">
                Our financial experts analyze market trends and identify the most promising 
                investment opportunities in historic properties.
              </p>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg text-center card-hover fade-in-up stagger-2">
              <div className="w-24 h-24 bg-golden rounded-full flex items-center justify-center mx-auto mb-4 scale-in hover-scale">
                <Building className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Restoration Experts</h3>
              <p className="text-gray-300">
                Skilled craftsmen and architects who understand both traditional techniques 
                and modern building standards.
              </p>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg text-center card-hover fade-in-up stagger-3">
              <div className="w-24 h-24 bg-golden rounded-full flex items-center justify-center mx-auto mb-4 scale-in hover-scale">
                <Award className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Project Managers</h3>
              <p className="text-gray-300">
                Experienced professionals who ensure every project is completed on time, 
                within budget, and to the highest quality standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-golden text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6 fade-in-up">Ready to Partner With Us?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto fade-in-up stagger-1">
            Join our network of successful investors and help us preserve architectural 
            heritage while building your wealth. Let's discuss how we can work together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up stagger-2">
            <a href="/contact" className="bg-white text-golden hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors hover-lift">
              Get Started Today
            </a>
            <a href="/developments" className="border-2 border-white text-white hover:bg-white hover:text-golden px-8 py-4 rounded-lg font-semibold transition-colors hover-lift">
              View Our Portfolio
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
